import axios from 'core/axiosInstance';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useDispatch as useDispatchRedux,
    useSelector as useSelectorRedux
} from 'react-redux';

export const DAYS_OF_WEEK = [{
    index: 0,
    longLabel: 'Domingo',
    shortLabel: 'Dom',
    value: 'sunday'
}, {
    index: 1,
    longLabel: 'Lunes',
    shortLabel: 'Lun',
    value: 'monday'
}, {
    index: 2,
    longLabel: 'Martes',
    shortLabel: 'Mar',
    value: 'tuesday'
}, {
    index: 3,
    longLabel: 'Miércoles',
    shortLabel: 'Mié',
    value: 'wednesday'
}, {
    index: 4,
    longLabel: 'Jueves',
    shortLabel: 'Jue',
    value: 'thursday'
}, {
    index: 5,
    longLabel: 'Viernes',
    shortLabel: 'Vie',
    value: 'friday'
}, {
    index: 6,
    longLabel: 'Sábado',
    shortLabel: 'Sáb',
    value: 'saturday'
}];

export const createFormData = body => {
    const formData = new FormData();
    Object.keys(body).forEach(key => {
        formData.append(key, body[key]);
    });
    return formData;
};

export const useArray = (initialState, config = {
    type: 'default'
}) => {
    const { type } = config;

    const [state, setState] = useState(initialState);

    const find = (id, value) =>
        type === 'object'
            ? state.find(element => element[id] === value)
            : undefined;

    const flatBy = parser => typeof parser === 'string'
        ? state.map(elem => elem[parser])
        : state.map(elem => parser(elem));

    const includes = (value, id = 'id') =>
        type === 'object'
            ? !!state.find(element => element[id] === value)
            : state.includes(value);

    const push = element => setState(state => [...state, element]);

    const remove =
        type === 'object'
            ? (key, value) => setState(state => state.filter(elem => elem[key] !== value))
            : value => setState(state => state.filter(elem => elem !== value));

    const removeByIndex = index => setState(state => state.filter((e, eIndex) => eIndex !== index));

    const replace = (next, previous, id = 'id') => {
        const newState = type === 'object' ? state.filter(elem => elem[id] !== previous[id]) : state.filter(elem => elem !== previous);
        newState.push(next);
        setState(newState);
    };

    const replaceValues = (nextValues = {}, key = 'id', value) => {
        if (value === undefined || type !== 'object') return undefined;
        const index = key === 'index' ? value : state.findIndex(elem => elem[key] === value);
        if (index < 0) return undefined;
        setState(state => {
            const newState = [...state];
            newState[index] = { ...newState[index], ...nextValues };
            return newState;
        });
    };
            
    const toggle = (element, id = 'id') => {
        if (type === 'object') {
            if (includes(element[id], id)) {
                remove(id, element[id]);
            } else {
                push(element);
            }
        } else {
            if (state.includes(element)) {
                remove(element);
            } else {
                push(element);
            }
        }
    };
    
    const functions = {
        find,
        flatBy,
        includes,
        push,
        remove,
        removeByIndex,
        replace,
        replaceValues,
        toggle
    };
    return [state, setState, functions];
};

export const useAudio = url => {
    const [audio] = useState(new Audio(url));
    const [playing, setPlaying] = useState(false);

    const pause = () => setPlaying(false);
    const play = () => setPlaying(true);
    const toggle = () => setPlaying(!playing);

    useEffect(() => {
        try {
            if (playing) {
                audio.loop = true;
                audio.play();
            } else {
                audio.pause();
                audio.loop = false;
                audio.currentTime = 0;
            }
        } catch (e) {}
    }, [audio, playing]);

    const playOnce = () => {
        try {
            audio.loop = false;
            audio.play();
        } catch (e) {}
    };

    useEffect(() => {
        const stopPlaying = setPlaying(false);
        audio.addEventListener('ended', stopPlaying);
        return () => {
            audio.removeEventListener('ended', stopPlaying);
        };
    }, [audio]);

    return [playing, { pause, play, playOnce, toggle }];
};

export const useAudioInputDevices = () => {
    const devices = useDevices();
    return devices.filter(device => device.kind === 'audioinput');
};

export const useAudioOutputDevices = () => {
    const devices = useDevices();
    return devices.filter(device => device.kind === 'audiooutput');
};

export const useCheckbox = (initialValue, inputProps = {}) => {
    const [state, setState] = useState(initialValue);
    const handleChange = () => setState(state => !state);
    const input = {
        ...inputProps,
        checked: !!state,
        onChange: handleChange,
        onClick: handleChange
    };
    return [input, state, setState];
};

const copyToClipboard = text => {
    try {
        navigator.clipboard.writeText(text);
        return true;
    } catch (e) {
        return false;
    }
};

export const useCopyToClipboard = config => {

    const {
        delay = 1000
    } = config ?? {};

    const [result, setResult] = useState(false);

    useEffect(() => {
        if (result) {
            setTimeout(() => setResult(false), delay);
        }
    }, [result]);

    const copy = text => {
        const done = copyToClipboard(text);
        if (!done) return;
        setResult(true);
    };

    return [copy, result];

};

export const useCountdown = (initCount, time) => {
    const [count, setCount] = useState(time);

    useEffect(() => {
        initCount && setInterval(() => {
            setCount(prevState => prevState - 1);
        }, 1000);
    }, [initCount]);

    const resetCountdown = () => setCount(time);

    return [count, { resetCountdown }];
};

export const useCron = (initialString = '* * * * * * 1') => {
    const SECONDS = 0;
    const MINUTES = 1;
    const HOURS = 2;
    const DAYS = 5;
    const WEEKS = 6;
    const [string, setRawString] = useState(initialString);

    const getDaysOfWeek = string => {
        if (string === '*') return DAYS_OF_WEEK.map(({ value }) => value);
        return string.split(',').map(elem => DAYS_OF_WEEK[elem].value);
    };

    const getDaysString = daysOfWeek => daysOfWeek.map(day => DAYS_OF_WEEK.find(({ value }) => value === day).index).sort();

    const getTime = array => {
        const hours = array[HOURS] === '*' ? '00' : array[HOURS];
        const minutes = array[MINUTES] === '*' ? '00' : array[MINUTES];
        const seconds = array[SECONDS] === '*' ? '00' : array[SECONDS];
        return `${ hours }:${ minutes }:${ seconds }`;
    };

    const parseString = useCallback(string => {
        const valuesArray = string.split(' ');
        const newValues = {
            daysOfWeek: getDaysOfWeek(valuesArray[DAYS]),
            time: getTime(valuesArray),
            weeks: Number(valuesArray[WEEKS])
        };
        return newValues;
    }, []);

    const [parsedString, setParsedString, setParsedStringValue] = useObjectState(parseString(initialString));

    const getCronString = useCallback(() => {
        const returnArray = initialString.split(' ');
        returnArray[0] = '00';
        returnArray[1] = parsedString['time'].split(':')[1];
        returnArray[2] = parsedString['time'].split(':')[0];
        returnArray[5] = getDaysString(parsedString['daysOfWeek']);
        returnArray[6] = parsedString['weeks'];
        return returnArray.join(' ');
    }, [parsedString]);

    const setRawStringValues = useCallback(object => {
        const oldStringArray = string.split(' ');
        Object.keys(object).forEach(index => {
            oldStringArray[index] = object[index];
        });
        setRawString(oldStringArray.join(' '));
    }, [string]);

    const setString = string => {
        setRawString(string);
        setParsedString(parseString(string));
    };

    const setTime = value => {
        setParsedStringValue('time', value);
        setRawStringValues({
            0: '0',
            1: value.split(':')[1],
            2: value.split(':')[0]
        });
    };

    const setWeek = weekNumber => {
        setParsedStringValue('weeks', weekNumber);
        setRawStringValues({
            6: weekNumber
        });
    };

    const setWeekDay = weekDay => {
        setParsedStringValue('daysOfWeek', [DAYS_OF_WEEK[weekDay].value]);
        setRawStringValues({
            5: weekDay
        });
    };

    const toHumanString = (value = string) => {
        const { daysOfWeek, time, weeks } = parseString(value);
        const shortDays = daysOfWeek.map(day => DAYS_OF_WEEK.find(({ value }) => value === day)?.shortLabel).join(', ');
        return `${ shortDays } ${ time.slice(0, 5) } cada ${ weeks } ${ weeks !== 1 ? 'semanas' : 'semana' }`;
    };

    const toggleWeekDay = weekDay => {
        if (!DAYS_OF_WEEK.find(({ value }) => value === weekDay)) return;
        const { daysOfWeek: prevDaysOfWeek } = parsedString;
        const newDaysOfWeek = prevDaysOfWeek.includes(weekDay)
            ? prevDaysOfWeek.filter(elem => elem !== weekDay)
            : [...prevDaysOfWeek, weekDay];
        setParsedStringValue('daysOfWeek', newDaysOfWeek);
        const daysString = getDaysString(newDaysOfWeek);
        setRawStringValues({
            5: daysString.join(',')
        });
    };

    return [string, parsedString, {
        getCronString,
        setString,
        setTime,
        setWeek,
        setWeekDay,
        toHumanString,
        toggleWeekDay
    }];
};

export const useDevices = () => {
    const [devices, setDevices] = useState([]);

    useEffect(() => {
        const getDevices = async () => {
            try {
                const auxDevices = await navigator.mediaDevices.enumerateDevices();
                setDevices(auxDevices);
            } catch (e) {
                setDevices([]);
            }
        };
        navigator.mediaDevices.addEventListener('devicechange', getDevices);
        getDevices();

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', getDevices);
        };
    }, []);

    return devices;
};

export const useDispatch = useDispatchRedux;

export const useDocumentVisibility = () => {
    const [visibility, setVisibility] = useState(document.visibilityState);
    useEffect(() => {
        const onVisibilityChange = () => setVisibility(document.visibilityState);
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);
    return visibility;
};

export const useErrors = () => {
    const [errors, setErrors] = useState({});

    const clearError = id => setErrors(errors => {
        delete errors[id];
        return errors;
    });
    
    const clearErrors = () => setErrors({});

    const setError = (id, err) => setErrors(errors => ({ ...errors, [id]: err }));

    return {
        clearError,
        clearErrors,
        errors,
        setError,
        setErrors
    };
};

export const useFileUpload = () => {

    const [loading, setLoading] = useState(false);

    const upload = async file => {
        try {
            setLoading(true);
            const { name, type, ...rest } = file;
            const url = '/file/store';
            const formData = createFormData({ ...rest, file, name, type });
            const { data } = await axios.post(url, formData, { headers: { 'content-type': 'multipart/form-data' } });
            return {
                data: data?.data,
                success: true
            };
        } catch (e) {
            return {
                error: e,
                success: false
            };
        } finally {
            setLoading(false);
        }
    };

    return [upload, loading];
};

export const useInput = (initialValue, inputOptions, callBack, preControl) => {
    const [state, setState] = useState(initialValue);
    const handleChange = value => {
        if (!preControl || preControl(value)) {
            !!callBack && callBack(value);
            setState(value);
        }
    };
    const input = {
        ...inputOptions,
        onChange: handleChange,
        value: state
    };
    return [input, state, setState];
};

export const useLocalstorage = (key, { defaultValue, type } = {}) => {
    const getLocalItem = () => {
        switch (type) {
        case ('object'): return JSON.parse(localStorage.getItem(key)) ?? defaultValue ?? {};
        default: return localStorage.getItem(key) ?? defaultValue;
        }
    };
    const setLocalItem = value => {
        switch (type) {
        case ('object'): localStorage.setItem(key, JSON.stringify(value));
            break;
        default: localStorage.setItem(key, value);
            break;
        }
    };
    const [state, setState] = useState(getLocalItem(key, type));

    const setValue = value => {
        setLocalItem(value);
        setState(value);
    };

    return [state, setValue];
};

export const useObjectState = initialValue => {
    const [state, setState] = useState(initialValue);
    const setValue = (id, value) => {
        setState(state => ({ ...state, [id]: value }));
    };
    const setValues = newValues => {
        setState(state => ({ ...state, ...newValues }));
    };
    return [state, setState, setValue, setValues];
};

export const usePrinter = config => {
    const { print } = config ?? {};
    const printRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        copyStyles: true,
        print
    });
    return [printRef, handlePrint];
};

export const useQuery = () => {
    const query = new URLSearchParams(useLocation().search);
    const result = {};
    query.forEach((value, key) => result[key] = value);
    return result;
};

export const useSelector = reducer => {
    const selector = useSelectorRedux(typeof reducer === 'string' ? state => state[reducer] : reducer);
    return selector;
};

export const useTableInput = (initialValue = [], {
    allowDelete = true,
    config = [],
    onAppend,
    rowActions = []
}) => {
    const [state, setState, { push, removeByIndex, replaceValues }] = useArray(initialValue, { type: 'object' });

    const parsedConfig = useMemo(() => config.map(c => ({
        ...c,
        onChange: ({ index, key, value }) => replaceValues({ [key]: value }, 'index', index),
        type: c?.type ?? 'INPUT'
    })), [config]);

    const parsedRowActions = useMemo(() => allowDelete ? [...rowActions, {
        icon: 'delete',
        onClick: ({ tableIndex }) => removeByIndex(tableIndex)
    }] : [...rowActions], [rowActions, allowDelete]);

    const tableInput = {
        config: parsedConfig,
        data: state,
        onAppend: onAppend ? () => onAppend(push) : () => push({}),
        rowActions: parsedRowActions
    };
    return [tableInput, state, setState];
};

export const useTableOrder = ({
    defaultBy = null,
    defaultWay = null
} = {}) => {
    const [orderBy, setOrderBy] = useState(defaultBy);
    const [orderWay, setOrderWay] = useState(defaultWay);

    const changeOrder = key => {
        if (orderBy !== key) {
            setOrderBy(key);
            setOrderWay('asc');
            return;
        }
        if (orderWay === 'desc') {
            setOrderBy(null);
            setOrderWay(null);
            return;
        }
        setOrderBy(key);
        setOrderWay(orderWay === 'asc' ? 'desc' : 'asc');
    };

    const orderIcon = key => {
        if (!key || orderBy !== key || orderWay === null) return undefined;
        return orderWay === 'asc' ? 'expand_less' : 'expand_more';
    };

    return [orderBy, orderWay, changeOrder, orderIcon];
};

export const useThrottle = (func, interval) => {
    const [blocked, setBlocked] = useState(false);

    const throttledFunction = useCallback((...args) => {
        if (blocked) return;
        func(...args);
        setBlocked(true);

        const timeoutId = setTimeout(() => {
            setBlocked(false);
        }, interval);

        return () => clearTimeout(timeoutId);
    }, [blocked, func, interval]);

    return throttledFunction;
};

export const useToggle = initialValue => {
    const [state, setState] = useState(initialValue);
    const toggle = () => setState(state => !state);
    return [state, toggle, setState];
};

export const useVideoInputDevices = () => {
    const devices = useDevices();
    return devices.filter(device => device.kind === 'videoinput');
};

export const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState({
        height: undefined,
        lg: false,
        md: false,
        width: undefined,
        xs: false
    });

    useEffect(() => {
        const handleResize = () => {
            const innerHeight = window.innerHeight;
            const innerWidth = window.innerWidth;
            setWindowSize({
                height: innerHeight,
                lg: innerWidth >= 768,
                md: innerWidth < 768 && innerWidth >= 576,
                width: window.innerWidth,
                xs: innerWidth < 576
            });
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    return windowSize;
};
